import { type ReactNode } from 'react';
import { AlertCircle, Inbox, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type StatePanelTone = 'loading' | 'empty' | 'error';

interface StatePanelProps {
    tone: StatePanelTone;
    title: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    className?: string;
}

const toneIcon = {
    loading: Loader2,
    empty: Inbox,
    error: AlertCircle,
};

const toneClass: Record<StatePanelTone, string> = {
    loading: 'text-tg-teal bg-tg-teal/10',
    empty: 'text-tg-subtle bg-tg-surface-2',
    error: 'text-tg-danger bg-tg-danger/10',
};

export function StatePanel({ tone, title, description, action, className }: StatePanelProps): JSX.Element {
    const Icon = toneIcon[tone];

    return (
        <div className={cn('flex min-h-48 flex-col items-center justify-center px-6 py-10 text-center', className)}>
            <div className={cn('grid h-11 w-11 place-items-center rounded-xl', toneClass[tone])}>
                <Icon className={cn('h-[22px] w-[22px]', tone === 'loading' && 'animate-spin')} />
            </div>
            {/* Centred flex-col children size to content: cap + break so a long error/URL never overflows. */}
            <div className="mt-4 max-w-full text-sm font-bold text-tg-ink [overflow-wrap:anywhere]">{title}</div>
            {description && <div className="mt-1 max-w-full text-sm text-tg-subtle [overflow-wrap:anywhere] sm:max-w-md">{description}</div>}
            {action && <div className="mt-4 flex max-w-full flex-wrap justify-center gap-2">{action}</div>}
        </div>
    );
}

